import { createSlice, createSelector } from '@reduxjs/toolkit';
import { orderData } from './cartSlice';
import { RootState } from './types';
import { roundToNumericPrecision } from '../utils/libs';
export interface PartialOrderDetails {
  order: orderData[];
  orderID: string;
  ReceiptID: string;
  loading: boolean;
  orderCustomerName: string;
  toBePaid: number;
  alreadyPaid: number;
  partialPaymentsTotalAmount: number;
  partialPaymentsTotalItems: number;
  /** Full order total (paid + unpaid). Passed from CheckoutScreen so the
   * partial-checkout receipt can label 153,800 as "Order Total" and the
   * selected-items subtotal (87,400) as "Sub-Total". */
  fullOrderTotal: number;
}
export interface PartialPaymentsSliceObject {
  partialpaymentsdetails: PartialOrderDetails;
}

const initalState: PartialPaymentsSliceObject = {
  partialpaymentsdetails: {
    order: [],
    orderID: '',
    ReceiptID: '',
    loading: false,
    orderCustomerName: '',
    toBePaid: 0,
    alreadyPaid: 0,
    partialPaymentsTotalAmount: 0,
    partialPaymentsTotalItems: 0,
    fullOrderTotal: 0,
  },
};
const calculateTotal = (order: orderData[]) => {
  return order.reduce((total, item) => {
    const {
      bp_product_information,
      bp_order_quantity,
      bp_order_paid_quantity,
      bp_order_mass,
      bp_order_paid_mass,
    } = item;
    const { bp_product_selling_price, bp_product_price_type } =
      bp_product_information;

    const unpaidQuantity = bp_order_quantity - (bp_order_paid_quantity ?? 0);

    if (bp_product_price_type === 'unit') {
      // For unit pricing, use mass multiplied by price
      if (bp_order_mass > 0) {
        const itemMass = bp_order_mass - (bp_order_paid_mass ?? 0);
        const itemPrice =
          bp_product_selling_price * roundToNumericPrecision(itemMass);
        return total + itemPrice;
      } else {
        return total + 0;
      }
    } else {
      // Default to item pricing (original behavior)
      const itemPrice = bp_product_selling_price * unpaidQuantity;
      return total + itemPrice;
    }
  }, 0);
};

const calculateTotalItems = (order: orderData[]) => {
  return order.reduce((total, item) => {
    const unpaidQuantity =
      item.bp_order_quantity - (item.bp_order_paid_quantity ?? 0);
    if (item.bp_product_information.bp_product_price_type === 'unit') {
      if (item.bp_order_mass && item.bp_order_mass > 0) {
        return total + 1;
      } else {
        return total;
      }
    } else {
      if (item.bp_order_quantity && item.bp_order_quantity > 0) {
        return total + unpaidQuantity;
      } else {
        return total;
      }
    }
  }, 0);
};

const sameOrderLineIdentity = (
  currentLine: orderData,
  payloadLine: orderData,
) => {
  const currentProduct = currentLine.bp_product_information;
  const payloadProduct = payloadLine.bp_product_information;
  return (
    currentProduct._id === payloadProduct._id &&
    (currentProduct.bp_variant_id ?? null) ===
      (payloadProduct.bp_variant_id ?? null)
  );
};

const partialpaymentsReducer = createSlice({
  name: 'partialpayments',
  initialState: initalState,
  reducers: {
    addPartialPaymentsDetails: (state, action) => {
      state.partialpaymentsdetails = action.payload;
      state.partialpaymentsdetails.fullOrderTotal =
        action.payload.fullOrderTotal ?? 0;
      state.partialpaymentsdetails.partialPaymentsTotalAmount = calculateTotal(
        state.partialpaymentsdetails.order,
      );
      state.partialpaymentsdetails.partialPaymentsTotalItems =
        calculateTotalItems(state.partialpaymentsdetails.order);
    },
    addPartialPaymentsOrderQuantity: (state, action) => {
      state.partialpaymentsdetails.order.forEach(item => {
        if (sameOrderLineIdentity(item, action.payload)) {
          item.bp_order_quantity += 1;
        }
      });
      state.partialpaymentsdetails.partialPaymentsTotalAmount = calculateTotal(
        state.partialpaymentsdetails.order,
      );
      state.partialpaymentsdetails.partialPaymentsTotalItems =
        calculateTotalItems(state.partialpaymentsdetails.order);
    },
    setPartialPaymentsOrderQuantity: (state, action) => {
      state.partialpaymentsdetails.order.forEach(item => {
        if (sameOrderLineIdentity(item, action.payload)) {
          item.bp_order_quantity = action.payload.bp_order_quantity;
        }
      });
      state.partialpaymentsdetails.partialPaymentsTotalAmount = calculateTotal(
        state.partialpaymentsdetails.order,
      );
      state.partialpaymentsdetails.partialPaymentsTotalItems =
        calculateTotalItems(state.partialpaymentsdetails.order);
    },
    addPartialPaymentsOrderMass: (state, action) => {
      state.partialpaymentsdetails.order.forEach(item => {
        if (item.bp_product_information.bp_product_price_type === 'unit') {
          if (sameOrderLineIdentity(item, action.payload)) {
            item.bp_order_mass = action.payload.bp_order_mass;
          }
        }
      });
      state.partialpaymentsdetails.partialPaymentsTotalAmount = calculateTotal(
        state.partialpaymentsdetails.order,
      );
      state.partialpaymentsdetails.partialPaymentsTotalItems =
        calculateTotalItems(state.partialpaymentsdetails.order);
    },
    subtractPartialPaymentsOrderQuantity: (state, action) => {
      state.partialpaymentsdetails.order.forEach(item => {
        if (sameOrderLineIdentity(item, action.payload)) {
          item.bp_order_quantity -= 1;
        }
      });
      state.partialpaymentsdetails.partialPaymentsTotalAmount = calculateTotal(
        state.partialpaymentsdetails.order,
      );
      state.partialpaymentsdetails.partialPaymentsTotalItems =
        calculateTotalItems(state.partialpaymentsdetails.order);
    },
    clearPartialPaymentsDetails: state => {
      state.partialpaymentsdetails = initalState.partialpaymentsdetails;
    },
  },
});
// Memoized Partial Payments Selectors
export const selectPartialPaymentDetails = createSelector(
  [(state: RootState) => state.partialpayments.partialpaymentsdetails],
  details => details,
);

export const selectPartialPaymentOrder = createSelector(
  [selectPartialPaymentDetails],
  details => details.order,
);

export const selectPartialPaymentTotalAmount = createSelector(
  [selectPartialPaymentDetails],
  details => details.partialPaymentsTotalAmount,
);

export const selectPartialPaymentTotalItems = createSelector(
  [selectPartialPaymentDetails],
  details => details.partialPaymentsTotalItems,
);

export const selectPartialPaymentOrderId = createSelector(
  [selectPartialPaymentDetails],
  details => details.orderID,
);

export const selectPartialPaymentCustomerName = createSelector(
  [selectPartialPaymentDetails],
  details => details.orderCustomerName,
);

export const selectPartialPaymentAmounts = createSelector(
  [selectPartialPaymentDetails],
  details => ({
    toBePaid: details.toBePaid,
    alreadyPaid: details.alreadyPaid,
    remaining: details.toBePaid - details.alreadyPaid,
  }),
);

export const {
  addPartialPaymentsDetails,
  addPartialPaymentsOrderQuantity,
  setPartialPaymentsOrderQuantity,
  subtractPartialPaymentsOrderQuantity,
  clearPartialPaymentsDetails,
  addPartialPaymentsOrderMass,
} = partialpaymentsReducer.actions;
export default partialpaymentsReducer.reducer;
